/**
 * Where a comment lives (RFC 0339).
 *
 * A comment about a page is in that page's comments document, and a comment
 * about a session in the session's. The page or session links it by
 * `commentsId`. A comment on something that is not a document (a code file,
 * whose id is a path) goes in the project's own comments document. The project keeps an index for lists (`commentIndex`) and,
 * until the move (3e) has run everywhere, the comments it held before
 * (`ProjectDoc.comments`), read after the thing and never written.
 */

import {
  type AnyDocumentId,
  Automerge,
  type DocHandle,
  type DocumentId,
  isValidDocumentId,
  type Repo,
} from '@automerge/automerge-repo/slim'
import { changeProject } from '../project/scalars'
import type { ProjectDoc } from '../project/types'
import {
  privateSessionWorldId,
  usesPrivateSessionCatalog,
} from '../session/catalog'
import { imm, sameStr } from '../stored-string'
import type {
  CommentEntry,
  CommentIndexEntry,
  CommentsDoc,
  CommentTarget,
} from './types'

/** What a comment is about, and so where it is stored. */
export interface CommentSubject {
  /** `project` holds comments on things that are not documents: a code file
   *  (`code:<ref>:<path>`), whose id no repo can open. */
  kind: 'document' | 'session' | 'project'
  /** The doc that links the comments document. */
  id: string
}

/** A comments map, the shape `projectComments` and `projectThreads` read. */
export interface CommentMap {
  comments: Record<string, CommentEntry>
}

/**
 * The page or session a target is about, or null when it names neither. A
 * page wins over a session, so a comment on a document opened from a session
 * is on the document. A `documentId` that is not a document id (a code file's
 * path) is about `projectId`, and without one it has nowhere to be.
 */
export function subjectOf(
  target: CommentTarget | Record<string, unknown> | undefined,
  projectId?: string,
): CommentSubject | null {
  const t = (target ?? {}) as Record<string, unknown>
  const documentId = t.documentId ? String(t.documentId) : ''
  if (documentId) {
    if (isValidDocumentId(documentId))
      return { kind: 'document', id: documentId }
    return projectId ? { kind: 'project', id: projectId } : null
  }
  const sessionId = t.sessionId ? String(t.sessionId) : ''
  if (sessionId) return { kind: 'session', id: sessionId }
  return null
}

/**
 * Every comments document a page or session links. Normally one. Two writers
 * who each made the first comment at once leave `commentsId` in conflict, and
 * the loser's comments are still real, so both are read.
 */
export function linkedCommentsIds(doc: unknown): string[] {
  if (!doc || typeof doc !== 'object') return []
  const out = new Set<string>()
  const own = (doc as { commentsId?: unknown }).commentsId
  if (own) out.add(String(own))
  try {
    const conflicts = Automerge.getConflicts(doc as never, 'commentsId')
    for (const v of Object.values(conflicts ?? {})) if (v) out.add(String(v))
  } catch {
    // A plain object, not an Automerge doc: it has no conflicts to read.
  }
  return [...out]
}

/**
 * A map field read with every concurrent value of it merged in, by key.
 *
 * Two devices that each create a map at the same absent field (a page's first
 * pairing, a project's first index entry) leave the field in conflict, and a
 * plain read sees only the winner: the loser's keys are not merged, they are
 * hidden. Reading every value keeps them. The winner's value wins on a key
 * both hold. Writes go to the winner; a writer that changes an entry it found
 * only in a loser copies it across first (`ensureInWinner`).
 */
export function mapWithConflicts<T = unknown>(
  doc: unknown,
  key: string,
): Record<string, T> {
  if (!doc || typeof doc !== 'object') return {}
  const out: Record<string, T> = {}
  try {
    const conflicts = Automerge.getConflicts(doc as never, key)
    for (const v of Object.values(conflicts ?? {})) {
      if (v && typeof v === 'object') Object.assign(out, v as Record<string, T>)
    }
  } catch {
    // A plain object, not an Automerge doc: it has no conflicts to read.
  }
  const own = (doc as Record<string, unknown>)[key]
  if (own && typeof own === 'object')
    Object.assign(out, own as Record<string, T>)
  return out
}

/** Remove index rows from every concurrent copy of the project map. A first
 * comment written on two peers can leave two map values in conflict; deleting
 * from only the winning value would keep the other peer's row discoverable. */
export function removeCommentIndexEntries(
  project: DocHandle<ProjectDoc>,
  ids: readonly string[],
): void {
  if (ids.length === 0) return
  const doc = project.doc()
  let copies = 0
  try {
    copies = Object.keys(
      Automerge.getConflicts(doc as never, 'commentIndex') ?? {},
    ).length
  } catch {
    copies = 0
  }
  const drop = new Set(ids)
  if (copies > 1) {
    const merged = mapWithConflicts<CommentIndexEntry>(doc, 'commentIndex')
    const kept: Record<string, CommentIndexEntry> = {}
    for (const [id, entry] of Object.entries(merged)) {
      if (!drop.has(id)) kept[id] = JSON.parse(JSON.stringify(entry))
    }
    changeProject(project, (draft) => {
      draft.commentIndex = kept
    })
    return
  }
  changeProject(project, (draft) => {
    for (const id of ids) {
      if (draft.commentIndex?.[id]) delete draft.commentIndex[id]
    }
  })
}

/** A handle, ready, or null when it does not arrive within the deadline. */
export async function readyHandle<T>(
  repo: Repo,
  id: string,
  timeoutMs = 4_000,
): Promise<DocHandle<T> | null> {
  try {
    const read = (async () => {
      const handle = await repo.find<T>(id as AnyDocumentId)
      await (handle as { whenReady?: () => Promise<void> }).whenReady?.()
      return handle
    })()
    return await Promise.race([
      read,
      new Promise<null>((r) => setTimeout(() => r(null), timeoutMs)),
    ])
  } catch {
    return null
  }
}

/**
 * The comments document a new comment on `subject` goes in, made and linked
 * when the subject has none yet.
 *
 * Made on the first comment rather than with the page, so a page nobody
 * comments on costs no second document. Under RFC 0338 a viewer cannot write
 * `commentsId` onto a page, and that is when it moves to creation.
 */
export async function commentsDocFor(
  repo: Repo,
  subject: CommentSubject,
): Promise<DocHandle<CommentsDoc>> {
  const owner = await readyHandle<{ commentsId?: string }>(repo, subject.id)
  if (!owner) {
    throw new Error(`${subject.kind} ${subject.id} is not available here`)
  }
  const existing = owner.doc()?.commentsId
  if (existing) {
    const handle = await readyHandle<CommentsDoc>(repo, String(existing))
    if (!handle) throw new Error(`comments ${existing} are not available here`)
    if (protectedRepo(repo) && !boundTo(handle, subject))
      throw new Error(`comments ${existing} belong to something else`)
    return handle
  }
  if (usesPrivateSessionCatalog(repo)) {
    throw new Error(
      'Comments must be prepared by the subject owner before sharing',
    )
  }
  const made = repo.create<CommentsDoc>({ subjectId: subject.id, comments: {} })
  owner.change((d) => {
    if (!d.commentsId) d.commentsId = made.documentId
  })
  const linked = String(owner.doc()?.commentsId ?? made.documentId)
  if (linked === made.documentId) return made
  return (await readyHandle<CommentsDoc>(repo, linked)) ?? made
}

/** The comments documents a subject links, opened. Missing ones are skipped. */
export async function subjectCommentDocs(
  repo: Repo,
  subject: CommentSubject,
): Promise<DocHandle<CommentsDoc>[]> {
  const owner = await readyHandle(repo, subject.id)
  if (!owner) return []
  const handles = await Promise.all(
    linkedCommentsIds(owner.doc()).map((id) =>
      readyHandle<CommentsDoc>(repo, id),
    ),
  )
  // RFC 0345 3g: a protected subject's comments must name it back. A
  // repointed commentsId cannot pass another document off as its comments.
  const opened = handles.filter(
    (h): h is DocHandle<CommentsDoc> =>
      h !== null && (!protectedRepo(repo) || boundTo(h, subject)),
  )
  rememberHomes(repo, subject, opened)
  return opened
}

// Called, not aliased: the catalog module may still be loading in a cycle.
const protectedRepo = (repo: Repo) => usesPrivateSessionCatalog(repo)
const boundTo = (handle: DocHandle<CommentsDoc>, subject: CommentSubject) =>
  String(handle.doc()?.subjectId ?? '') === subject.id

/** RFC 0345 3g: a protected project keeps no comment index, so the repo
 * remembers the subject of each comment it has read. Local only: never
 * synced, never written to a shared document. */
const commentHomes = new WeakMap<Repo, Map<string, CommentSubject>>()

function rememberHomes(
  repo: Repo,
  subject: CommentSubject,
  handles: DocHandle<CommentsDoc>[],
): void {
  if (!usesPrivateSessionCatalog(repo)) return
  let homes = commentHomes.get(repo)
  if (!homes) {
    homes = new Map()
    commentHomes.set(repo, homes)
  }
  for (const handle of handles)
    for (const id of Object.keys(handle.doc()?.comments ?? {}))
      homes.set(id, subject)
}

/**
 * The project whose index a subject's comments go in: the one a page names as
 * its own, when that project lists it; otherwise the caller's. A session does
 * not name its project, so its comments index where the caller says.
 */
export async function indexProjectFor(
  repo: Repo,
  subject: CommentSubject,
  fallback: string,
): Promise<string> {
  if (subject.kind === 'project') return subject.id
  if (subject.kind !== 'document') return fallback
  const doc = await readyHandle<{ workspaceId?: string }>(repo, subject.id)
  const named = doc?.doc()?.workspaceId ? String(doc.doc()?.workspaceId) : ''
  if (!named || named === fallback) return fallback
  const project = await readyHandle<ProjectDoc>(repo, named)
  return project?.doc()?.documents?.[subject.id] ? named : fallback
}

/** The index entry for a comment, as plain scalars. */
export function indexEntryOf(
  comment: CommentEntry,
  subject: CommentSubject,
): CommentIndexEntry {
  // On the project's own comments, the id the comment names (a code file's)
  // is what a list filters by, and `subjectOf` routes it back here.
  const where =
    subject.kind === 'document'
      ? { documentId: imm(subject.id) }
      : subject.kind === 'session'
        ? { sessionId: imm(subject.id) }
        : { documentId: imm(comment.target?.documentId ?? '') }
  const anchorText = comment.target?.anchorText
  return {
    ...where,
    ...(anchorText ? { anchorText: imm(anchorText) } : {}),
    ...(comment.parentId ? { parentId: imm(comment.parentId) } : {}),
    author: imm(comment.author ?? ''),
    ...(comment.authorSessionId
      ? { authorSessionId: imm(comment.authorSessionId) }
      : {}),
    resolved: comment.resolved === true,
    createdAt: Number(comment.createdAt ?? 0),
  }
}

/**
 * Write index entries, and drop the ones named in `remove`. Second, always,
 * after the comment itself (3d), so a crash between the two leaves a comment
 * the index is missing, which a reader repairs, never an index entry pointing
 * at nothing written.
 */
export async function writeCommentIndex(
  repo: Repo,
  projectId: string,
  entries: Record<string, CommentIndexEntry>,
  remove: string[] = [],
): Promise<void> {
  // A protected project is shared with readers; a comment must never write it
  // or publish metadata from a private session into it. Discovery is local.
  if (usesPrivateSessionCatalog(repo)) return
  if (Object.keys(entries).length === 0 && remove.length === 0) return
  const project = await readyHandle<ProjectDoc>(repo, projectId)
  if (!project) return
  changeProject(project, (d) => {
    if (!d.commentIndex) d.commentIndex = {}
    for (const id of remove) delete d.commentIndex[id]
    // Into the winning map, even for an entry a conflicting copy holds: that
    // is what reconciles the two.
    for (const [id, entry] of Object.entries(entries)) {
      if (!sameEntry(d.commentIndex[id], entry)) d.commentIndex[id] = entry
    }
  })
}

function sameEntry(
  stored: CommentIndexEntry | undefined,
  b: CommentIndexEntry,
): boolean {
  const a = stored
  if (!a) return false
  // BY VALUE, NOT BY WRAPPER. These fields are written as `ImmutableString`,
  // and two wrappers holding the same characters are different objects: `===`
  // would answer "changed" every time and rewrite the whole index on every
  // pass, which is the opposite of what the wrapper is for.
  return (
    sameStr(a.documentId, b.documentId) &&
    sameStr(a.sessionId, b.sessionId) &&
    sameStr(a.anchorText, b.anchorText) &&
    sameStr(a.parentId, b.parentId) &&
    sameStr(a.author, b.author) &&
    sameStr(a.authorSessionId, b.authorSessionId) &&
    a.resolved === b.resolved &&
    a.createdAt === b.createdAt
  )
}

/**
 * Where a comment was found, and the map it is in.
 *
 * `handles` is EVERY linked comments document holding the id. Normally one;
 * two when two devices each made the page's first comments document and both
 * copied the same comment there (the move on two machines at once). A change
 * applies to every copy, so a resolve or a delete cannot land on the copy a
 * reader discards. `handle` is the one a new reply goes beside.
 */
export type CommentHome =
  | {
      kind: 'comments'
      handle: DocHandle<CommentsDoc>
      handles: DocHandle<CommentsDoc>[]
      subject: CommentSubject
    }
  | { kind: 'legacy'; handle: DocHandle<ProjectDoc> }

/**
 * Find a comment by id: through the project's index to its comments document,
 * then in the project's leftovers. Null when neither has it.
 */
export async function locateComment(
  repo: Repo,
  projectId: string,
  commentId: string,
): Promise<CommentHome | null> {
  if (usesPrivateSessionCatalog(repo)) {
    const hinted = commentHomes.get(repo)?.get(commentId)
    const tried = new Set<string>()
    const holdingIn = async (subject: CommentSubject) => {
      tried.add(subjectKey(subject))
      const holding = (await subjectCommentDocs(repo, subject)).filter(
        (h) => h.doc()?.comments?.[commentId],
      )
      return holding[0]
        ? ({
            kind: 'comments',
            handle: holding[0],
            handles: holding,
            subject,
          } as const)
        : null
    }
    for (const subject of [
      ...(hinted ? [hinted] : []),
      ...loadedCommentSubjects(repo),
    ]) {
      if (tried.has(subjectKey(subject))) continue
      const home = await holdingIn(subject)
      if (home) return home
    }
    // A cold repo: open what the catalogs list, in parallel, and take the
    // first that holds it.
    const rest = (await catalogCommentSubjects(repo, projectId)).filter(
      (s) => !tried.has(subjectKey(s)),
    )
    for (const home of await Promise.all(rest.map(holdingIn)))
      if (home) return home
    return null
  }
  const project = await readyHandle<ProjectDoc>(repo, projectId)
  const doc = project?.doc()
  const entry = mapWithConflicts<CommentIndexEntry>(doc, 'commentIndex')[
    commentId
  ]
  const subject = entry
    ? subjectOf(
        { documentId: entry.documentId, sessionId: entry.sessionId },
        projectId,
      )
    : null
  if (subject) {
    const holding = (await subjectCommentDocs(repo, subject)).filter(
      (h) => h.doc()?.comments?.[commentId],
    )
    const first = holding[0]
    if (first) {
      return { kind: 'comments', handle: first, handles: holding, subject }
    }
  }
  if (project && doc?.comments?.[commentId]) {
    return { kind: 'legacy', handle: project }
  }
  return null
}

/**
 * Every comment that could match `target`, as one map: the subject's comments
 * documents, then the project's leftovers by id (3e). With no page or session
 * in the target, every subject the project's index names is read.
 *
 * The thing wins on an id both hold, so a comment mid-move is shown once,
 * unless the move has not indexed it yet: then the project's entry is the
 * current one (`midMove`).
 */
export async function loadComments(
  repo: Repo,
  projectId: string,
  target?: CommentTarget | Record<string, string | undefined>,
): Promise<CommentMap> {
  const subject = subjectOf(target, projectId)
  if (usesPrivateSessionCatalog(repo)) {
    const subjects = subject
      ? [subject]
      : uniqueSubjects([
          ...loadedCommentSubjects(repo, projectId),
          ...(await catalogCommentSubjects(repo, projectId)),
        ])
    const handles = await Promise.all(
      subjects.map((s) => subjectCommentDocs(repo, s)),
    )
    return mergeComments(
      undefined,
      handles.flat().map((h) => h.doc()),
    )
  }
  const project = await readyHandle<ProjectDoc>(repo, projectId)
  const doc = project?.doc()
  const subjects = subject ? [subject] : indexedSubjects(doc, projectId)
  const merged: Record<string, CommentEntry> = {}
  for (const [id, c] of Object.entries(doc?.comments ?? {})) merged[id] = c
  const docs = await Promise.all(
    subjects.map((s) => subjectCommentDocs(repo, s)),
  )
  const index = mapWithConflicts(doc, 'commentIndex')
  for (const handle of docs.flat()) {
    for (const [id, c] of Object.entries(handle.doc()?.comments ?? {})) {
      if (midMove(id, doc?.comments, index)) continue
      merged[id] = c
    }
  }
  return { comments: merged }
}

/**
 * A comment the move has copied but not yet taken off the project: still in
 * the project's map and not indexed. Until it is indexed every write lands on
 * the project's entry, so that one is current and the copy may be stale.
 */
function midMove(
  id: string,
  legacy: Record<string, unknown> | undefined,
  index: Record<string, unknown>,
): boolean {
  return Boolean(legacy?.[id]) && !index[id]
}

/** Each page or session the project's index has a comment on, once. */
function indexedSubjects(
  doc: ProjectDoc | undefined,
  projectId: string,
): CommentSubject[] {
  const seen = new Map<string, CommentSubject>()
  const index = mapWithConflicts<CommentIndexEntry>(doc, 'commentIndex')
  for (const entry of Object.values(index)) {
    const s = subjectOf(
      { documentId: entry?.documentId, sessionId: entry?.sessionId },
      projectId,
    )
    if (s) seen.set(`${s.kind}:${s.id}`, s)
  }
  return [...seen.values()]
}

/**
 * What a count reads for some subjects. On an open repo, the project's index
 * (3d), with no comments document opened. A protected project keeps no index
 * (RFC 0345 3g), so there it is the subjects' own comments documents, read
 * the way `loadComments` reads one target.
 */
export async function countedComments(
  repo: Repo,
  projectId: string,
  projectDoc: unknown,
  subjectIds: readonly string[],
): Promise<CommentMap> {
  if (!usesPrivateSessionCatalog(repo)) return commentListView(projectDoc)
  const maps = await Promise.all(
    subjectIds.map((documentId) =>
      loadComments(repo, projectId, { documentId }),
    ),
  )
  return { comments: Object.assign({}, ...maps.map((m) => m.comments)) }
}

/**
 * The comments a LIST reads, from the project alone: its index, shown as
 * comments with no body, and its leftovers, which keep theirs (3d).
 *
 * For counts, ids and who is waiting on whom. A reader that shows a body opens
 * the comments document (`loadComments`).
 */
export function commentListView(projectDoc: unknown): CommentMap {
  const doc = (projectDoc ?? {}) as ProjectDoc
  const out: Record<string, CommentEntry> = {}
  const index = mapWithConflicts<CommentIndexEntry>(doc, 'commentIndex')
  for (const [id, raw] of Object.entries(index)) {
    if (!raw || typeof raw !== 'object') continue
    const e = raw as CommentIndexEntry
    const target: CommentTarget = e.documentId
      ? { documentId: String(e.documentId) }
      : e.sessionId
        ? { sessionId: String(e.sessionId) }
        : {}
    if (e.anchorText) target.anchorText = String(e.anchorText)
    out[id] = {
      id,
      target,
      author: String(e.author ?? ''),
      body: '',
      resolved: e.resolved === true,
      createdAt: Number(e.createdAt ?? 0),
      updatedAt: Number(e.createdAt ?? 0),
      ...(e.parentId ? { parentId: String(e.parentId) } : {}),
      ...(e.authorSessionId
        ? { authorSessionId: String(e.authorSessionId) }
        : {}),
    }
  }
  for (const [id, c] of Object.entries(doc.comments ?? {})) out[id] = c
  return { comments: out }
}

/**
 * A subject's comments documents merged with the project's leftovers, for a
 * reader that already holds them (the window's hooks). Same rule as
 * `loadComments`: the thing wins.
 */
export function mergeComments(
  projectDoc: unknown,
  commentDocs: unknown[],
): CommentMap {
  const merged: Record<string, CommentEntry> = {}
  const legacy = (projectDoc as { comments?: Record<string, CommentEntry> })
    ?.comments
  const index = mapWithConflicts(projectDoc, 'commentIndex')
  for (const [id, c] of Object.entries(legacy ?? {})) merged[id] = c
  for (const d of commentDocs) {
    const map = (d as { comments?: Record<string, CommentEntry> } | undefined)
      ?.comments
    for (const [id, c] of Object.entries(map ?? {})) {
      if (midMove(id, legacy, index)) continue
      merged[id] = c
    }
  }
  return { comments: merged }
}

type PrivateCatalog = {
  privateSessions?: Record<string, { projectId: string }>
}

/** The person's own World, when this repo has bound and loaded it. */
function loadedPrivateCatalog(repo: Repo): PrivateCatalog | undefined {
  const worldId = privateSessionWorldId(repo)
  const worldHandle = worldId ? repo.handles[worldId as DocumentId] : undefined
  return worldHandle?.isReady()
    ? (worldHandle.doc() as PrivateCatalog)
    : undefined
}

const subjectKey = (s: CommentSubject) => `${s.kind}:${s.id}`

function uniqueSubjects(subjects: CommentSubject[]): CommentSubject[] {
  return [...new Map(subjects.map((s) => [subjectKey(s), s])).values()]
}

/** Every subject the catalogs list for a project: the project itself, its
 * pages and shared sessions, and this person's private sessions in it from
 * their own World. Read, never written, so nothing private reaches the
 * project. Opening each costs a find per page, paid only on a cold lookup
 * or an unscoped list. */
async function catalogCommentSubjects(
  repo: Repo,
  projectId: string,
): Promise<CommentSubject[]> {
  const subjects: CommentSubject[] = []
  const project = await readyHandle<ProjectDoc>(repo, projectId)
  const doc = project?.doc()
  if (doc) {
    subjects.push({ kind: 'project', id: projectId })
    for (const id of Object.keys(doc.documents ?? {}))
      subjects.push({ kind: 'document', id })
    for (const id of doc.sessionIds ?? [])
      subjects.push({ kind: 'session', id: String(id) })
  }
  const world = loadedPrivateCatalog(repo)
  for (const [id, entry] of Object.entries(world?.privateSessions ?? {}))
    if (String(entry?.projectId) === projectId)
      subjects.push({ kind: 'session', id })
  return uniqueSubjects(subjects)
}

/** Subjects this repo already has open: the first place a protected lookup
 * looks. Never discovers private sessions through a shared project index.
 */
function loadedCommentSubjects(
  repo: Repo,
  projectId?: string,
): CommentSubject[] {
  const world = loadedPrivateCatalog(repo)
  const subjects: CommentSubject[] = []
  for (const handle of Object.values(repo.handles)) {
    if (!handle.isReady()) continue
    const doc = handle.doc() as Record<string, unknown> | undefined
    if (!doc || !linkedCommentsIds(doc).length) continue
    if (
      projectId &&
      handle.documentId !== projectId &&
      String(doc.workspaceId ?? '') !== projectId &&
      world?.privateSessions?.[handle.documentId]?.projectId !== projectId
    )
      continue
    subjects.push({
      id: handle.documentId,
      kind: doc.path ? 'document' : doc.sessionIds ? 'project' : 'session',
    })
  }
  return subjects
}
